import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ScrollingBanner from '@/components/ScrollingBanner';
import axios from 'axios';
import { Heart, CreditCard, Send, Banknote, Building2, Smartphone, Boxes, HandHeart, Globe2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDER = "À renseigner par l'admin";

export default function DonationsPage() {
  const [formType, setFormType] = useState('simple');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    amount: '',
    message: ''
  });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/public`);
      setSettings(res.data);
    } catch (e) {
      console.error('Failed to load donation settings:', e);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSimpleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/donations/request`, formData);
      toast.success('Demande de don envoyée avec succès !');
      setFormData({ name: '', email: '', phone: '', amount: '', message: '' });
    } catch (error) {
      console.error('Error submitting donation request:', error);
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setLoading(false);
    }
  };

  const handleStripeCheckout = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount < 1) {
        toast.error('Veuillez entrer un montant valide (minimum $1)');
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/donations/checkout`, {
        amount: amount,
        name: formData.name,
        email: formData.email,
        origin_url: window.location.origin
      });

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Erreur lors de la création du paiement');
      setLoading(false);
    }
  };

  const crowdfundingLinks = useMemo(() => {
    if (!settings) return [];
    return [
      { url: settings.crowdfunding_globalgiving, label: 'GlobalGiving', desc: 'Plateforme internationale pour ONG' },
      { url: settings.crowdfunding_helloasso, label: 'HelloAsso', desc: 'Plateforme française associative (sans commission)' },
      { url: settings.crowdfunding_gofundme, label: 'GoFundMe', desc: 'Crowdfunding universel' },
      { url: settings.crowdfunding_ulule, label: 'Ulule', desc: 'Projets communautaires & solidaires' },
      { url: settings.crowdfunding_other_url, label: settings.crowdfunding_other_name || 'Autre plateforme', desc: 'Partenaire CRESA' },
    ].filter((p) => p.url);
  }, [settings]);

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster />
      <Navigation />
      <ScrollingBanner />

      <section className="section-padding bg-white" data-testid="donations-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="donations-title"
            >
              Soutenez Notre Mission
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto leading-relaxed">
              Votre contribution aide à propulser les îlots de développement intégral auto-gérés en RD Congo.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Payment channels table */}
            <div className="card mb-10" data-testid="donation-channels-table">
              <h2 className="text-2xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Modalités de soutien
              </h2>
              <p className="text-sm text-[#5C665A] mb-4">
                Plusieurs façons de soutenir CRESA ASBL. L&apos;administration met à jour régulièrement les coordonnées ci-dessous.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-[#4A6B48] text-left">
                      <th className="py-3 px-2 text-[#2E332D] font-bold">Type de don</th>
                      <th className="py-3 px-2 text-[#2E332D] font-bold">Modalité</th>
                      <th className="py-3 px-2 text-[#2E332D] font-bold">Coordonnées</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#2E332D]">
                    <tr className="border-b border-[#E5E3DB]">
                      <td rowSpan={4} className="py-3 px-2 align-top">
                        <div className="font-bold inline-flex items-center gap-2"><Banknote size={16} className="text-[#4A6B48]" /> Don en espèce</div>
                      </td>
                      <td className="py-3 px-2 inline-flex items-center gap-2"><Smartphone size={14} className="text-[#D87D4A]" /> Mobile Money — Airtel</td>
                      <td className="py-3 px-2 font-mono text-xs">{settings?.mobile_money_airtel || PLACEHOLDER}</td>
                    </tr>
                    <tr className="border-b border-[#E5E3DB]">
                      <td className="py-3 px-2 inline-flex items-center gap-2"><Smartphone size={14} className="text-[#D87D4A]" /> Mobile Money — Orange</td>
                      <td className="py-3 px-2 font-mono text-xs">{settings?.mobile_money_orange || PLACEHOLDER}</td>
                    </tr>
                    <tr className="border-b border-[#E5E3DB]">
                      <td className="py-3 px-2 inline-flex items-center gap-2"><Smartphone size={14} className="text-[#D87D4A]" /> Mobile Money — Vodacom (M-Pesa)</td>
                      <td className="py-3 px-2 font-mono text-xs">{settings?.mobile_money_vodacom || PLACEHOLDER}</td>
                    </tr>
                    <tr className="border-b border-[#E5E3DB]">
                      <td className="py-3 px-2 inline-flex items-center gap-2"><Building2 size={14} className="text-[#D87D4A]" /> Virement bancaire (IBAN)</td>
                      <td className="py-3 px-2 font-mono text-xs">
                        {settings?.bank_iban ? (
                          <>
                            <div>{settings.bank_iban}</div>
                            {settings?.bank_name && <div className="text-[#5C665A]">{settings.bank_name}</div>}
                            {settings?.bank_swift && <div className="text-[#5C665A]">SWIFT/BIC : {settings.bank_swift}</div>}
                          </>
                        ) : PLACEHOLDER}
                      </td>
                    </tr>
                    <tr className="border-b border-[#E5E3DB]">
                      <td className="py-3 px-2 align-top">
                        <div className="font-bold inline-flex items-center gap-2"><Boxes size={16} className="text-[#4A6B48]" /> Don en nature</div>
                      </td>
                      <td className="py-3 px-2">Matériel agricole, semences, livres, équipement informatique</td>
                      <td className="py-3 px-2 text-xs">{settings?.donation_in_kind_contact || settings?.contact_email || PLACEHOLDER}</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-2 align-top">
                        <div className="font-bold inline-flex items-center gap-2"><HandHeart size={16} className="text-[#4A6B48]" /> Don en services</div>
                      </td>
                      <td className="py-3 px-2">Bénévolat, expertise, formation gratuite, traduction</td>
                      <td className="py-3 px-2 text-xs">{settings?.donation_services_contact || settings?.contact_email || PLACEHOLDER}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Crowdfunding platforms */}
            {settings && (
              (settings.crowdfunding_globalgiving || settings.crowdfunding_helloasso ||
               settings.crowdfunding_gofundme || settings.crowdfunding_ulule ||
               settings.crowdfunding_other_url) && (
                <div className="card mb-10" data-testid="crowdfunding-section">
                  <h2 className="text-2xl font-bold text-[#2E332D] mb-2 inline-flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    <Globe2 className="text-[#D87D4A]" /> Plateformes de financement participatif
                  </h2>
                  <p className="text-sm text-[#5C665A] mb-4">
                    Soutenez CRESA via ces plateformes de collecte alignées avec notre modèle de développement
                    intégral et auto-géré. Cliquez pour faire un don depuis n&apos;importe où dans le monde.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {crowdfundingLinks.map((p) => (
                      <a
                        key={p.label}
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`crowdfunding-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className="flex items-start gap-3 p-4 bg-[#F9F8F6] hover:bg-[#4A6B48]/5 border-2 border-[#E5E3DB] hover:border-[#4A6B48] rounded-lg transition-all"
                      >
                        <div className="w-10 h-10 rounded-full bg-[#D87D4A]/10 flex items-center justify-center shrink-0">
                          <Globe2 className="text-[#D87D4A]" size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#2E332D] inline-flex items-center gap-1">
                            {p.label} <ExternalLink size={11} className="text-[#5C665A]" />
                          </p>
                          <p className="text-xs text-[#5C665A]">{p.desc}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )
            )}

            {/* Form Type Selector */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex rounded-lg border border-[#E5E3DB] p-1">
                <button
                  onClick={() => setFormType('simple')}
                  data-testid="form-type-simple"
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                    formType === 'simple'
                      ? 'bg-[#4A6B48] text-white'
                      : 'text-[#5C665A] hover:text-[#4A6B48]'
                  }`}
                >
                  <Send className="inline mr-2" size={16} />
                  Demande simple
                </button>
                <button
                  onClick={() => setFormType('stripe')}
                  data-testid="form-type-stripe"
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                    formType === 'stripe'
                      ? 'bg-[#4A6B48] text-white'
                      : 'text-[#5C665A] hover:text-[#4A6B48]'
                  }`}
                >
                  <CreditCard className="inline mr-2" size={16} />
                  Paiement en ligne
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="card max-w-2xl mx-auto">
              {formType === 'simple' ? (
                <form onSubmit={handleSimpleSubmit} data-testid="simple-donation-form">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        data-testid="donation-name-input"
                        className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A6B48]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        data-testid="donation-email-input"
                        className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A6B48]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        data-testid="donation-phone-input"
                        className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A6B48]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">
                        Montant souhaité (USD)
                      </label>
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        min="1"
                        step="0.01"
                        data-testid="donation-amount-input"
                        className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A6B48]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        data-testid="donation-message-input"
                        className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A6B48]"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="submit-simple-donation"
                      className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleStripeCheckout} data-testid="stripe-donation-form">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        data-testid="stripe-name-input"
                        className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A6B48]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        data-testid="stripe-email-input"
                        className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A6B48]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">
                        Montant du don (USD) *
                      </label>
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        required
                        min="1"
                        step="0.01"
                        placeholder="10.00"
                        data-testid="stripe-amount-input"
                        className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A6B48]"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="submit-stripe-donation"
                      className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Redirection...' : 'Procéder au paiement'}
                    </button>
                    <p className="text-xs text-center text-[#5C665A] mt-2">
                      Paiement sécurisé par Stripe
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
